import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import RightSlideModal from '../../components/RightSlideModal';

export default function ClassTeacherPage() {
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ classId: '', sectionId: '', teacherId: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, sectionsRes, teachersRes, assignmentsRes] = await Promise.all([
        api.get('/classes'),
        api.get('/sections'),
        api.get('/teachers'),
        api.get('/class-teachers')
      ]);
      setClasses(classesRes.data);
      setSections(sectionsRes.data);
      setTeachers(teachersRes.data);
      setAssignments(assignmentsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/class-teachers', formData);
      toast.success('Class teacher assigned successfully');
      setShowModal(false);
      setFormData({ classId: '', sectionId: '', teacherId: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign class teacher');
    }
  };

  const getSectionsForClass = (classId) => {
    return sections.filter(s => s.classId === classId);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit font-semibold text-2xl text-slate-800">Class Teacher Assignment</h2>
          <p className="text-slate-500">Assign one class teacher per section</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 py-2.5 font-medium shadow-sm active:scale-95 transition-all flex items-center gap-2">
          <UserCheck className="w-5 h-5" />
          Assign Class Teacher
        </button>
      </div>

      <div className="space-y-4">
        {classes.map(cls => {
          const classSections = getSectionsForClass(cls.id);
          return (
            <div key={cls.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
              <h3 className="font-outfit font-semibold text-lg text-slate-800 mb-4">{cls.className}</h3>
              {classSections.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {classSections.map(section => {
                    const assignment = assignments.find(a => a.sectionId === section.id);
                    const teacher = teachers.find(t => t.id === assignment?.teacherId);
                    return (
                      <div key={section.id} className="p-4 bg-green-50 border border-green-100 rounded-xl">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-slate-800">{section.sectionName}</p>
                            {teacher ? (
                              <div className="mt-2">
                                <p className="text-sm text-green-700 font-medium">{teacher.name}</p>
                                <p className="text-xs text-green-600">{teacher.email}</p>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-500 mt-2">No teacher assigned</p>
                            )}
                          </div>
                          <button onClick={() => { setFormData({ classId: cls.id, sectionId: section.id, teacherId: teacher?.id || '' }); setShowModal(true); }} className="text-xs text-green-600 hover:text-green-700 font-medium">
                            {teacher ? 'Change' : 'Assign'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No sections available for this class</p>
              )}
            </div>
          );
        })}
      </div>

      <RightSlideModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)} 
        title="Assign Class Teacher"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Class</label>
            <select value={formData.classId} onChange={(e) => setFormData({...formData, classId: e.target.value, sectionId: ''})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all outline-none" required>
              <option value="">Select Class</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.className}</option>)}
            </select>
          </div>
          {formData.classId && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Section</label>
              <select value={formData.sectionId} onChange={(e) => setFormData({...formData, sectionId: e.target.value})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all outline-none" required>
                <option value="">Select Section</option>
                {getSectionsForClass(formData.classId).map(s => <option key={s.id} value={s.id}>{s.sectionName}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Teacher</label>
            <select value={formData.teacherId} onChange={(e) => setFormData({...formData, teacherId: e.target.value})} className="w-full px-4 py-2 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-green-100 focus:border-green-500 transition-all outline-none" required>
              <option value="">Select Teacher</option>
              {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white rounded-full py-2.5 font-medium">Assign Teacher</button>
            <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-slate-200 hover:bg-slate-50 rounded-full py-2.5 font-medium">Cancel</button>
          </div>
        </form>
      </RightSlideModal>
    </div>
  );
}
